import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import semverLt from 'semver/functions/lt';
import semverValid from 'semver/functions/valid';

import { anykrowdApi } from '@/api/anykrowdApi';
import { useAuthStore } from '@/stores/auth/authStore';
import useUtils from '@/shared/composables/useUtils';

export const MIN_NATIVE_SAFETY_VERSION = '2.2.10';

export interface BrowserHandoffResponse {
	url: string;
	expires_in: number;
}

export interface BrowserHandoffConsumeResponse {
	token: string;
	redirect_path: string;
}

export const isLegacySafetyNativeVersion = (version?: string | null): boolean => {
	if (!version) {
		return true;
	}

	const normalizedVersion = semverValid(version);

	if (!normalizedVersion) {
		return true;
	}

	return semverLt(normalizedVersion, MIN_NATIVE_SAFETY_VERSION);
};

export const shouldUseSafetyBrowserHandoff = async (): Promise<boolean> => {
	if (!Capacitor.isNativePlatform()) {
		return false;
	}

	try {
		const appInfo = await CapApp.getInfo();
		return isLegacySafetyNativeVersion(appInfo.version);
	} catch {
		return true;
	}
};

export const createSafetyBrowserHandoff = async (redirectPath: string): Promise<BrowserHandoffResponse> => {
	const { data } = await anykrowdApi.post<{ code: string; data: BrowserHandoffResponse }>(
		'/safety/browser-handoff',
		{
			redirect_path: redirectPath,
		},
		true,
	);

	return data.data;
};

export const consumeSafetyBrowserHandoff = async (token: string): Promise<BrowserHandoffConsumeResponse> => {
	const { data } = await anykrowdApi.post<{ code: string; data: BrowserHandoffConsumeResponse }>(`/safety/browser-handoff/${token}/consume`, {}, false);
	return data.data;
};

export const openSafetyBrowserHandoff = async (redirectPath: string): Promise<boolean> => {
	if (!useAuthStore().isAuthenticated()) {
		return false;
	}

	try {
		const handoff = await createSafetyBrowserHandoff(redirectPath);
		const { openURL } = useUtils();
		await openURL(handoff.url);

		return true;
	} catch {
		return false;
	}
};
